import { Button } from '@/components/ui/button'
import { BadgeButton } from '@/components/ui/badge'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { AppLinkProps } from '@/hooks/usePathHelper'
import type { CSSVariableStyle } from '@/lib/css-variables'
import { cn, parseCurrency } from '@/lib/utils'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
  type Column,
  type ColumnDef,
  type ColumnMeta,
  type Header,
  type OnChangeFn,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type Table,
  type TableOptions,
  type VisibilityState,
} from '@tanstack/react-table'
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  RotateCcw,
  Search,
  XIcon,
} from 'lucide-react'
import {
  cloneElement,
  isValidElement,
  memo,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ChangeEventHandler,
  type ComponentProps,
  type ComponentType,
  type CSSProperties,
  type DragEventHandler,
  type Key,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type PointerEventHandler,
  type ReactElement,
  type ReactNode,
} from 'react'
import {
  getDirectionalSortState,
  SortableColumnHeader,
  type DirectionalSortConfig,
  type SortableColumnHeaderProps,
} from '@/components/ui/sortable-column-header'

/** Semantic width buckets for table-mode columns (`meta.kind`). */
export type IndexTableColumnKind =
  | 'avatar'
  | 'actions'
  | 'badge'
  | 'date'
  | 'id'
  | 'money'
  | 'number'
  | 'primary'
  | 'text'

// Avatar columns supply their own widths and keep the existing fallback for unconfigured kinds.
type IndexTableWidthKind = Exclude<IndexTableColumnKind, 'avatar'>

export type IndexTableAlign = 'start' | 'center' | 'end'

export type IndexTableHeaderTone = 'brand' | 'success' | 'warning' | 'destructive' | 'info'

export type IndexDataTableRowTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info'
  | 'muted'

/** Column widths as CSS lengths; `width` sets both the preferred and the minimum width. */
export type IndexTableColumnWidths = {
  kind?: IndexTableColumnKind
  width?: string
  minWidth?: string
  preferredWidth?: string
}

/** Spreadsheet cell types `indexTableExport` formats; anything else exports as text. */
export type IndexTableExportType =
  | 'text'
  | 'number'
  | 'currency'
  | 'integer'
  | 'boolean'
  | 'date'
  | 'datetime'

export type IndexTableExportValue<TData> = (row: TData, context: { rowIndex: number }) => unknown

/** One spreadsheet column of a table column that exports as several (`meta.exportColumns`). */
export type IndexTableExportColumnConfig<TData> = {
  id?: string
  label?: string
  exportLabel?: string
  type?: IndexTableExportType
  exportType?: IndexTableExportType
  value?: IndexTableExportValue<TData>
  exportValue?: IndexTableExportValue<TData>
}

declare module '@tanstack/react-table' {
  // Column meta read by IndexDataTable, by indexTableExport, and by the disclosure group headers
  // some tables render (SalesGroupHeader).
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Declare a semantic kind first; reach for widths only when no kind fits. */
    kind?: IndexTableColumnKind
    width?: string
    minWidth?: string
    preferredWidth?: string
    /** Header, mobile cell label and spreadsheet header; defaults to a string `header`, then the id. */
    label?: string
    mobileLabel?: string
    icon?: SortableColumnHeaderProps['icon']
    /** Server-sort values; the header toggles between them through `onSortChange`. */
    sortable?: DirectionalSortConfig
    align?: IndexTableAlign
    headerAlign?: IndexTableAlign
    contentAlign?: IndexTableAlign
    headerTone?: IndexTableHeaderTone
    headerWrap?: boolean
    tooltip?: ReactNode
    tooltipClassName?: string
    headerClassName?: string
    headClassName?: string
    sortClassName?: string
    colClassName?: string
    cellClassName?: string
    contentClassName?: string
    linkClassName?: string
    /** `false` leaves this cell out of the row link; `'overlay'` puts the link under the content. */
    link?: boolean | 'overlay'
    /** Skeleton width class while the first page loads. */
    loadingWidth?: string
    exportable?: boolean
    exportLabel?: string
    exportType?: IndexTableExportType
    exportValue?: IndexTableExportValue<TData>
    exportColumns?: IndexTableExportColumnConfig<TData>[]
    description?: ReactNode
    expanded?: boolean
    onToggle?: () => void
  }
}

// Table-mode columns should declare `meta.kind` first, then reach for custom
// widths only when a semantic kind is not enough. The min-width map controls
// when horizontal scrolling begins; the preferred-width map guides the desktop
// colgroup when the table has room to breathe.
const indexTableColumnWidthByKind: Record<IndexTableWidthKind, string> = {
  actions: '2.5rem',
  badge: '7.25rem',
  date: '7.5rem',
  id: '7.5rem',
  money: '8.25rem',
  number: '5.5rem',
  primary: '12rem',
  text: '12rem',
}

const indexTableColumnMinWidthByKind: Record<IndexTableWidthKind, string> = {
  actions: '2.5rem',
  badge: '6rem',
  date: '7rem',
  id: '6rem',
  money: '7.5rem',
  number: '4.5rem',
  primary: '10rem',
  text: '10rem',
}

const indexTableHeaderAlignClassName: Record<IndexTableAlign, string> = {
  start: 'text-left',
  center: 'text-center',
  end: 'text-right',
}

const indexTableCellAlignClassName: Record<IndexTableAlign, string> = {
  start: '@[40rem]/index-table:text-left',
  center: '@[40rem]/index-table:text-center',
  end: '@[40rem]/index-table:text-right',
}

const indexTableCellPlacementClassName: Record<IndexTableAlign, string> = {
  start: '@[40rem]/index-table:justify-start',
  center: '@[40rem]/index-table:justify-center',
  end: '@[40rem]/index-table:justify-end',
}
const indexDataTableSkeletonWidths = ['w-36', 'w-48', 'w-24', 'w-32', 'w-20', 'w-28']
const emptyIndexDataTableSorting: SortingState = []

const indexDataTableRowToneClassName: Record<IndexDataTableRowTone, string> = {
  neutral: 'even:bg-elevated/45 hover:bg-muted/70',
  success:
    'bg-status-success-background even:bg-status-success-background hover:bg-status-success-border/45 dark:bg-status-success-background/35 dark:even:bg-status-success-background/35 dark:hover:bg-status-success-background/55',
  warning:
    'bg-status-warning-background even:bg-status-warning-background hover:bg-status-warning-border/45 dark:bg-status-warning-background/35 dark:even:bg-status-warning-background/35 dark:hover:bg-status-warning-background/55',
  destructive:
    'bg-status-destructive-background even:bg-status-destructive-background hover:bg-status-destructive-border/45 dark:bg-status-destructive-background/35 dark:even:bg-status-destructive-background/35 dark:hover:bg-status-destructive-background/55',
  info: 'bg-status-info-background even:bg-status-info-background hover:bg-status-info-border/45 dark:bg-status-info-background/35 dark:even:bg-status-info-background/35 dark:hover:bg-status-info-background/55',
  muted:
    'bg-muted/20 even:bg-muted/20 hover:bg-muted/35 dark:bg-muted/10 dark:even:bg-muted/10 dark:hover:bg-muted/25',
}

const neutralUnreadIndexDataTableRowClassName =
  'bg-primary/5 even:bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:even:bg-primary/10 dark:hover:bg-primary/15'

export type IndexDataTableRowPresentation = {
  tone?: IndexDataTableRowTone
  attention?: 'unread' | 'none'
  contentTone?: 'muted' | 'default'
  selected?: boolean
}

export function normalizeIndexDataTableRowPresentation(
  presentation?: IndexDataTableRowPresentation | null
): Required<IndexDataTableRowPresentation> {
  // JavaScript callers can send any tone; unknown tones render as neutral.
  const tone =
    presentation?.tone && Object.hasOwn(indexDataTableRowToneClassName, presentation.tone)
      ? presentation.tone
      : 'neutral'

  return {
    tone,
    attention: presentation?.attention === 'unread' ? 'unread' : 'none',
    contentTone: presentation?.contentTone === 'muted' ? 'muted' : 'default',
    selected: Boolean(presentation?.selected),
  }
}

export function getIndexDataTableRowPresentationClassName(
  presentation?: IndexDataTableRowPresentation | null
): string {
  const normalized = normalizeIndexDataTableRowPresentation(presentation)
  const toneClassName =
    normalized.tone === 'neutral' && normalized.attention === 'unread'
      ? neutralUnreadIndexDataTableRowClassName
      : indexDataTableRowToneClassName[normalized.tone]

  return cn(
    toneClassName,
    normalized.attention === 'unread' && 'border-l-interaction font-semibold',
    normalized.contentTone === 'muted' && 'text-muted-foreground',
    normalized.selected && 'shadow-[inset_0_0_0_2px_var(--color-selection-border)]'
  )
}

const indexTableDragScrollActivationDistance = 12
const indexTableDragScrollIgnoredTargetSelector = [
  'button',
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[role="button"]',
  '[data-slot="alert-dialog-content"]',
  '[data-slot="alert-dialog-overlay"]',
  '[data-slot="dialog-content"]',
  '[data-slot="dialog-overlay"]',
  '[data-slot="scroll-area-scrollbar"]',
  '[data-slot="scroll-area-thumb"]',
  '[data-index-table-drag-scroll-ignore]',
].join(',')

// A column id stands in for a missing kind (an `actions` column needs no meta), and JavaScript
// callers can name kinds without a width, so lookups take any string.
function isIndexTableColumnKind(kind: string | undefined): kind is IndexTableWidthKind {
  return kind !== undefined && Object.hasOwn(indexTableColumnWidthByKind, kind)
}

function getIndexTableKindLength(
  lengths: Record<IndexTableWidthKind, string>,
  kind: string | undefined
): string | undefined {
  return isIndexTableColumnKind(kind) ? lengths[kind] : undefined
}

function getIndexTableColumnWidth(column: IndexTableColumnWidths): string {
  return (
    column.preferredWidth ??
    column.width ??
    getIndexTableKindLength(indexTableColumnWidthByKind, column.kind) ??
    indexTableColumnWidthByKind.text
  )
}

// A TanStack column, a column definition, or a bare widths object.
type IndexDataTableWidthSource = IndexTableColumnWidths & {
  id?: string
  meta?: IndexTableColumnWidths
  columnDef?: { meta?: IndexTableColumnWidths }
}

function getIndexDataTableColumnMeta(column: IndexDataTableWidthSource): IndexTableColumnWidths {
  return column.columnDef?.meta ?? column.meta ?? column
}

function getIndexDataTableColumnKind(column: IndexDataTableWidthSource): string | undefined {
  const meta = getIndexDataTableColumnMeta(column)

  return meta.kind ?? column.id
}

function getIndexDataTablePreferredWidth(column: IndexDataTableWidthSource): string | undefined {
  const meta = getIndexDataTableColumnMeta(column)
  const kind = getIndexDataTableColumnKind(column)

  return (
    meta.preferredWidth ?? meta.width ?? getIndexTableKindLength(indexTableColumnWidthByKind, kind)
  )
}

function getIndexDataTableMinimumWidth(column: IndexDataTableWidthSource): string {
  const meta = getIndexDataTableColumnMeta(column)
  const kind = getIndexDataTableColumnKind(column)

  return (
    meta.minWidth ??
    meta.width ??
    getIndexTableKindLength(indexTableColumnMinWidthByKind, kind) ??
    indexTableColumnMinWidthByKind.text
  )
}

function getIndexTableCssLengthSum(lengths: Array<string | undefined>): string | undefined {
  const filteredLengths = lengths.filter(Boolean)

  if (!filteredLengths.length) {
    return undefined
  }

  if (filteredLengths.length === 1) {
    return filteredLengths[0]
  }

  return `calc(${filteredLengths.join(' + ')})`
}

export function getIndexDataTableStyle(
  columns?: readonly IndexDataTableWidthSource[] | null
): CSSVariableStyle | undefined {
  if (!columns?.length) {
    return undefined
  }

  return {
    '--index-data-table-min-width': getIndexTableCssLengthSum(
      columns.map(getIndexDataTableMinimumWidth)
    ),
    '--index-data-table-preferred-width': getIndexTableCssLengthSum(
      columns.map(
        (column) => getIndexDataTablePreferredWidth(column) ?? getIndexDataTableMinimumWidth(column)
      )
    ),
  }
}

export function getIndexTableGridStyle(
  columns?: readonly IndexTableColumnWidths[] | null
): CSSVariableStyle | undefined {
  if (!columns?.length) {
    return undefined
  }

  return {
    '--index-table-columns': columns.map(getIndexTableColumnWidth).join(' '),
  }
}

export function getIndexTableGridClassName(
  columns?: readonly IndexTableColumnWidths[] | null
): string | undefined {
  return columns?.length
    ? '@[40rem]/index-table:grid-cols-[var(--index-table-columns)] @[40rem]/index-table:justify-between'
    : undefined
}

export function getIndexTableContentStyle(
  columns?: readonly IndexTableColumnWidths[] | null
): CSSProperties | undefined {
  return columns?.length ? { minWidth: 'max-content' } : undefined
}

// The fallbacks below cover JavaScript callers that pass other values (such as 'right').
export function getIndexTableHeaderAlignClassName(align: IndexTableAlign = 'start'): string {
  return indexTableHeaderAlignClassName[align] ?? indexTableHeaderAlignClassName.start
}

export function getIndexTableCellAlignClassName(align: IndexTableAlign = 'start'): string {
  return indexTableCellAlignClassName[align] ?? indexTableCellAlignClassName.start
}

export function getIndexTableCellPlacementClassName(align: IndexTableAlign = 'start'): string {
  return indexTableCellPlacementClassName[align] ?? indexTableCellPlacementClassName.start
}

function getIndexTableSortableHeaderClassName(
  align: IndexTableAlign = 'start'
): string | undefined {
  if (align === 'center') {
    return 'justify-center text-center'
  }

  if (align === 'end') {
    return 'justify-end text-right'
  }

  return undefined
}

export type IndexPaginationMeta = {
  /** The response has not arrived yet. */
  isPlaceholder?: boolean
  current_page: number
  page_size: number
  total: number
  last_page: number
}

type IndexPaginationResponseMeta = {
  current_page?: number | null
  page_size?: number | null
  total?: number | null
  last_page?: number | null
}

/** A bare list, or a page of `data` (lists) or `rows` (reports) with optional `meta`. */
export type IndexPaginationResponse =
  | unknown[]
  | {
      meta?: IndexPaginationResponseMeta | null
      data?: unknown[] | null
      rows?: unknown[] | null
    }

// Every screen builds pagination metadata here, from raw query data. A response that has not
// arrived yet is marked isPlaceholder; defaulting it to [] first would lose that loading state.
export function getIndexPaginationMeta(
  response: IndexPaginationResponse | null | undefined,
  page: number,
  pageSize: number
): IndexPaginationMeta {
  if (!response || Array.isArray(response)) {
    const total = Array.isArray(response) ? response.length : 0

    return {
      isPlaceholder: !response,
      current_page: page,
      page_size: pageSize,
      total,
      last_page: Math.max(Math.ceil(total / pageSize), 1),
    }
  }

  return {
    current_page: response?.meta?.current_page ?? page,
    page_size: response?.meta?.page_size ?? pageSize,
    // Lists return `data`; reports return `rows`.
    total: response?.meta?.total ?? (response?.data ?? response?.rows)?.length ?? 0,
    last_page: response?.meta?.last_page ?? 1,
  }
}

export type IndexTableToolbarProps = {
  primary: ReactNode
  secondary?: ReactNode
  actions?: ReactNode
  className?: string
  /** IndexTable turns this off when it already wraps the toolbar in the container. */
  container?: boolean
  stackControlsUntilWide?: boolean
}

export function IndexTableToolbar({
  primary,
  secondary,
  actions,
  className,
  container = true,
  stackControlsUntilWide = false,
}: IndexTableToolbarProps) {
  return (
    <div
      data-slot="index-table-toolbar"
      className={cn(
        container && '@container/index-table-toolbar',
        'border-b bg-muted/10 px-3 py-3',
        className
      )}
    >
      <div
        className={cn(
          'flex flex-col flex-wrap gap-2',
          stackControlsUntilWide
            ? '@[92rem]/index-table-toolbar:flex-row @[92rem]/index-table-toolbar:items-center @[92rem]/index-table-toolbar:justify-between'
            : '@[40rem]/index-table-toolbar:flex-row @[40rem]/index-table-toolbar:items-center @[40rem]/index-table-toolbar:justify-between'
        )}
      >
        <div
          className={cn(
            'min-w-0 max-w-full shrink-0',
            stackControlsUntilWide && 'w-full @[92rem]/index-table-toolbar:w-auto'
          )}
        >
          {primary}
        </div>
        {secondary || actions ? (
          <div
            className={cn(
              'flex min-w-0 max-w-full flex-wrap gap-2',
              stackControlsUntilWide &&
                'w-full items-start @[92rem]/index-table-toolbar:w-auto @[92rem]/index-table-toolbar:items-center @[92rem]/index-table-toolbar:justify-end',
              !stackControlsUntilWide && 'items-center'
            )}
          >
            {secondary}
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function isIndexTableToolbarElement(node: ReactNode): node is ReactElement<IndexTableToolbarProps> {
  return isValidElement(node) && node.type === IndexTableToolbar
}

function IndexTableToolbarContainer({ toolbar }: { toolbar: ReactNode }) {
  if (!toolbar) {
    return null
  }

  const toolbarContent = isIndexTableToolbarElement(toolbar)
    ? cloneElement(toolbar, { container: false })
    : toolbar

  return <div className="@container/index-table-toolbar">{toolbarContent}</div>
}

export type IndexTableClearFiltersButtonProps = {
  /** Without a handler the button is not rendered. */
  onClick?: MouseEventHandler<HTMLButtonElement>
  label?: ReactNode
}

export function IndexTableClearFiltersButton({
  onClick,
  label = 'Clear filters',
}: IndexTableClearFiltersButtonProps) {
  if (typeof onClick !== 'function') {
    return null
  }

  return (
    <BadgeButton variant="filter" onClick={onClick}>
      <RotateCcw className="size-3" />
      {label}
    </BadgeButton>
  )
}

export type IndexTableExportButtonProps = {
  onClick?: MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  isPending?: boolean
  label?: string
  disabledLabel?: ReactNode
  pendingLabel?: ReactNode
  className?: string
}

export function IndexTableExportButton({
  onClick,
  disabled = false,
  isPending = false,
  label = 'Download spreadsheet',
  disabledLabel = 'Nothing to export.',
  pendingLabel = 'Preparing spreadsheet...',
  className,
}: IndexTableExportButtonProps) {
  const isDisabled = disabled || isPending
  const tooltipLabel = isPending ? pendingLabel : disabled ? disabledLabel : label
  const button = (
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label={label}
      disabled={isDisabled}
      className={className}
      onClick={onClick}
      aria-busy={isPending ? 'true' : undefined}
    >
      {isPending ? <LoadingSpinner className="size-4" /> : <Download className="size-4" />}
      <span className="sr-only">{label}</span>
    </Button>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {isDisabled ? (
          <span className="inline-flex" tabIndex={0}>
            {button}
          </span>
        ) : (
          button
        )}
      </TooltipTrigger>
      <TooltipContent>{tooltipLabel}</TooltipContent>
    </Tooltip>
  )
}

export type IndexTableSearchBoxProps = {
  /** The committed search; the box keeps its own draft while focused. */
  value?: string | null
  onCommit?: (value: string) => void
  countText?: ReactNode
  isFetching?: boolean
  placeholder?: string
  debounceMs?: number
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}

export function IndexTableSearchBox({
  value,
  onCommit,
  countText,
  isFetching,
  placeholder,
  debounceMs = 150,
  onKeyDown,
}: IndexTableSearchBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const pendingCommitRef = useRef<number | null>(null)
  const committedValue = value ?? ''
  const [isEditing, setIsEditing] = useState(false)
  const [draftState, setDraftState] = useState(() => ({
    value: committedValue,
    committedValue,
  }))
  let draftValue = draftState.value

  if (!isEditing && draftState.committedValue !== committedValue) {
    draftValue = committedValue
    setDraftState({ value: committedValue, committedValue })
  }

  const clearPendingCommit = () => {
    if (pendingCommitRef.current === null) {
      return
    }

    window.clearTimeout(pendingCommitRef.current)
    pendingCommitRef.current = null
  }

  const scheduleCommit = (nextValue: string) => {
    clearPendingCommit()

    pendingCommitRef.current = window.setTimeout(() => {
      pendingCommitRef.current = null
      onCommit?.(nextValue)
    }, debounceMs)
  }

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const nextValue = event.target.value
    setDraftState({ value: nextValue, committedValue })
    scheduleCommit(nextValue)
  }

  const handleInputFocus = () => {
    setIsEditing(true)
  }

  const handleInputBlur = () => {
    setIsEditing(false)
    clearPendingCommit()

    if (draftState.value !== committedValue) {
      onCommit?.(draftState.value)
    }

    setDraftState({
      value: draftState.value,
      committedValue,
    })
  }

  const clearSearch = () => {
    clearPendingCommit()
    setDraftState({ value: '', committedValue })
    onCommit?.('')
    inputRef.current?.focus()
  }

  useEffect(() => {
    return () => {
      clearPendingCommit()
    }
  }, [])

  return (
    <div className="border-input selection:bg-selection selection:text-selection-foreground focus-within:border-ring focus-within:ring-ring/50 flex h-10 w-full max-w-full items-center gap-2 rounded-full border bg-card px-3 py-1 text-base transition-[border-color,box-shadow] focus-within:ring-[3px] sm:w-104 md:text-sm">
      <Search className="size-4 shrink-0 stroke-muted-foreground" />
      <input
        ref={inputRef}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-placeholder-foreground"
        type="text"
        value={draftValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      <span
        aria-live="polite"
        className="shrink-0 whitespace-nowrap text-xs text-muted-foreground tabular-nums"
      >
        {countText}
        {isFetching ? <span className="sr-only"> updating</span> : null}
      </span>
      {draftValue ? (
        <Button
          variant="ghostMuted"
          size="icon-xs"
          className="-m-2"
          aria-label="Clear search"
          onClick={clearSearch}
        >
          <XIcon className="size-4" />
        </Button>
      ) : null}
    </div>
  )
}

function getIndexTableScrollViewport(root: Element): Element | null {
  return root.querySelector('[data-slot="scroll-area-viewport"]')
}

function getIndexTableScrollableViewport(root: Element): Element | null {
  const viewport = getIndexTableScrollViewport(root)

  return viewport && viewport.scrollWidth > viewport.clientWidth ? viewport : null
}

function isIndexTableDragScrollIgnoredTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element && Boolean(target.closest(indexTableDragScrollIgnoredTargetSelector))
  )
}

type IndexTableDragScrollState = {
  pointerId: number
  startX: number
  startY: number
  startScrollLeft: number
  viewport: Element
  isDragging: boolean
  previousUserSelect: string | null
  previousBodyCursor: string | null
  hasPointerCapture: boolean
}

function IndexTableScrollArea({
  children,
  scrollBarClassName,
  className,
}: {
  children?: ReactNode
  scrollBarClassName?: string
  className?: string
}) {
  const dragScrollStateRef = useRef<IndexTableDragScrollState | null>(null)
  const suppressClickRef = useRef(false)
  const suppressClickTimeoutRef = useRef<number | null>(null)
  const [isDragScrolling, setIsDragScrolling] = useState(false)

  const clearClickSuppression = () => {
    suppressClickRef.current = false

    if (suppressClickTimeoutRef.current !== null) {
      window.clearTimeout(suppressClickTimeoutRef.current)
      suppressClickTimeoutRef.current = null
    }
  }

  const suppressNextClick = () => {
    clearClickSuppression()

    suppressClickRef.current = true
    suppressClickTimeoutRef.current = window.setTimeout(clearClickSuppression, 250)
  }

  const restoreBodyDragStyles = (state: IndexTableDragScrollState) => {
    if (state.previousUserSelect !== null) {
      document.body.style.userSelect = state.previousUserSelect
      state.previousUserSelect = null
    }

    if (state.previousBodyCursor !== null) {
      document.body.style.cursor = state.previousBodyCursor
      state.previousBodyCursor = null
    }
  }

  const handlePointerDown: PointerEventHandler<HTMLDivElement> = (event) => {
    if (
      event.button !== 0 ||
      event.pointerType !== 'mouse' ||
      isIndexTableDragScrollIgnoredTarget(event.target)
    ) {
      return
    }

    const viewport = getIndexTableScrollableViewport(event.currentTarget)

    if (!viewport) {
      return
    }

    dragScrollStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: viewport.scrollLeft,
      viewport,
      isDragging: false,
      previousUserSelect: null,
      previousBodyCursor: null,
      hasPointerCapture: false,
    }
  }

  const handlePointerMove: PointerEventHandler<HTMLDivElement> = (event) => {
    const state = dragScrollStateRef.current

    if (!state || state.pointerId !== event.pointerId) {
      return
    }

    const deltaX = event.clientX - state.startX
    const deltaY = event.clientY - state.startY

    if (!state.isDragging) {
      if (
        Math.abs(deltaX) <= indexTableDragScrollActivationDistance ||
        Math.abs(deltaX) < Math.abs(deltaY)
      ) {
        return
      }

      state.isDragging = true
      state.previousUserSelect = document.body.style.userSelect
      state.previousBodyCursor = document.body.style.cursor
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'grabbing'
      setIsDragScrolling(true)
      event.currentTarget.setPointerCapture?.(event.pointerId)
      state.hasPointerCapture = true
    }

    event.preventDefault()

    const maxScrollLeft = state.viewport.scrollWidth - state.viewport.clientWidth
    state.viewport.scrollLeft = Math.max(0, Math.min(maxScrollLeft, state.startScrollLeft - deltaX))
  }

  const finishPointerDrag: PointerEventHandler<HTMLDivElement> = (event) => {
    const state = dragScrollStateRef.current

    if (!state || state.pointerId !== event.pointerId) {
      return
    }

    if (state.isDragging) {
      suppressNextClick()
    }

    restoreBodyDragStyles(state)
    setIsDragScrolling(false)

    if (state.hasPointerCapture) {
      event.currentTarget.releasePointerCapture?.(event.pointerId)
    }

    dragScrollStateRef.current = null
  }

  const handleClickCapture: MouseEventHandler<HTMLDivElement> = (event) => {
    if (!suppressClickRef.current) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    clearClickSuppression()
  }

  const handleDragStartCapture: DragEventHandler<HTMLDivElement> = (event) => {
    if (isIndexTableDragScrollIgnoredTarget(event.target)) {
      return
    }

    if (!getIndexTableScrollableViewport(event.currentTarget)) {
      return
    }

    event.preventDefault()
  }

  useEffect(() => {
    return () => {
      if (suppressClickTimeoutRef.current !== null) {
        window.clearTimeout(suppressClickTimeoutRef.current)
      }

      const state = dragScrollStateRef.current

      if (state) {
        restoreBodyDragStyles(state)
      }
    }
  }, [])

  return (
    <div className={cn('w-full', className)}>
      <ScrollArea
        className={cn(
          'h-full max-h-[inherit] w-full',
          isDragScrolling &&
            'cursor-grabbing select-none [&_a]:cursor-grabbing [&_button]:cursor-grabbing [&_[role=button]]:cursor-grabbing'
        )}
        // Carry the frame's max-height to the viewport so tall tables scroll instead of clipping.
        viewportClassName="max-h-[inherit]"
        data-index-table-drag-scroll
        data-index-table-dragging={isDragScrolling ? 'true' : undefined}
        onClickCapture={handleClickCapture}
        onDragStartCapture={handleDragStartCapture}
        onPointerCancel={finishPointerDrag}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerDrag}
      >
        {children}
        <ScrollBar orientation="horizontal" className={scrollBarClassName} />
      </ScrollArea>
    </div>
  )
}

export type IndexTableProps = {
  toolbar?: ReactNode
  header?: ReactNode
  isEmpty?: boolean
  emptyState?: ReactNode
  children?: ReactNode
  /** Grid layout: with semantic columns the content takes its intrinsic width, not a 1050px floor. */
  columns?: readonly IndexTableColumnWidths[]
  layout?: 'grid' | 'table'
  minWidthClassName?: string
  tableClassName?: string
  tableStyle?: CSSVariableStyle
  scrollAreaClassName?: string
  isLoading?: boolean
  quietBadges?: boolean
}

export function IndexTable({
  toolbar,
  header,
  isEmpty,
  emptyState,
  children,
  columns,
  layout = 'grid',
  minWidthClassName,
  tableClassName,
  tableStyle,
  scrollAreaClassName,
  isLoading = false,
  quietBadges = false,
}: IndexTableProps) {
  return (
    <div
      className={cn(
        '@container/index-table overflow-hidden rounded-xl border border-border bg-card',
        quietBadges &&
          '[&_[data-badge]]:border-transparent [&_[data-badge]]:font-medium [&_[data-badge]]:shadow-none'
      )}
      aria-busy={isLoading ? 'true' : undefined}
    >
      <IndexTableToolbarContainer toolbar={toolbar} />
      {isEmpty ? (
        <div className="p-5">{emptyState}</div>
      ) : layout === 'table' ? (
        <IndexTableScrollArea
          className={scrollAreaClassName}
          scrollBarClassName="hidden @[40rem]/index-table:flex"
        >
          <div className="w-full max-w-full overflow-hidden @[40rem]/index-table:overflow-visible">
            <table
              className={cn(
                'block w-full min-w-0 table-auto border-collapse @[40rem]/index-table:table @[40rem]/index-table:min-w-[var(--index-data-table-min-width,100%)]',
                tableClassName
              )}
              style={tableStyle}
            >
              {header}
              {children}
            </table>
          </div>
        </IndexTableScrollArea>
      ) : (
        <IndexTableScrollArea className={scrollAreaClassName}>
          <div
            className={cn(
              'w-full',
              !columns?.length && '@[40rem]/index-table:min-w-[1050px]',
              minWidthClassName
            )}
            style={getIndexTableContentStyle(columns)}
          >
            {header}
            <div className="divide-y">{children}</div>
          </div>
        </IndexTableScrollArea>
      )}
    </div>
  )
}

export type IndexTableHeaderProps = {
  children?: ReactNode
  columns?: readonly IndexTableColumnWidths[]
  gridClassName?: string
  className?: string
}

export function IndexTableHeader({
  children,
  columns,
  gridClassName,
  className,
}: IndexTableHeaderProps) {
  return (
    <div
      className={cn(
        'hidden gap-3 border-b bg-elevated/80 px-4 py-2.5 @[40rem]/index-table:grid @[40rem]/index-table:w-full',
        getIndexTableGridClassName(columns),
        gridClassName,
        className
      )}
      style={getIndexTableGridStyle(columns)}
    >
      {children}
    </div>
  )
}

const indexTableHeaderTones: Record<IndexTableHeaderTone, string> = {
  brand: 'bg-accentSecondary text-interaction',
  success: 'bg-status-success-background text-status-success-foreground',
  warning: 'bg-status-warning-background text-status-warning-foreground',
  destructive: 'bg-status-destructive-background text-status-destructive-foreground',
  info: 'bg-status-info-background text-status-info-foreground',
}

// `align` replaces the obsolete <th align> attribute.
export type IndexTableTableHeaderCellProps = Omit<ComponentProps<'th'>, 'align'> & {
  align?: IndexTableAlign
  tone?: IndexTableHeaderTone
}

export function IndexTableTableHeaderCell({
  children,
  align = 'start',
  tone,
  scope = 'col',
  className,
  ...props
}: IndexTableTableHeaderCellProps) {
  return (
    <th
      {...props}
      scope={scope}
      className={cn(
        'px-3 py-2.5 align-middle',
        getIndexTableHeaderAlignClassName(align),
        tone ? indexTableHeaderTones[tone] : undefined,
        className
      )}
    >
      {children}
    </th>
  )
}

export type IndexTableTableCellProps = {
  children?: ReactNode
  /** Shown above the content when rows stack on narrow containers. */
  label: ReactNode
  link?: AppLinkProps | null
  linkMode?: 'wrap' | 'overlay'
  align?: IndexTableAlign
  contentAlign?: IndexTableAlign
  className?: string
  contentClassName?: string
  linkClassName?: string
}

export function IndexTableTableCell({
  children,
  label,
  link,
  linkMode = 'wrap',
  align = 'start',
  contentAlign = align,
  className,
  contentClassName,
  linkClassName,
}: IndexTableTableCellProps) {
  const content = (
    <>
      <IndexTableMobileLabel>{label}</IndexTableMobileLabel>
      {/* Stacked table rows size to the card container, not the viewport, because sidebars can shrink the content area before the table query. */}
      <div
        className={cn(
          'w-full min-w-0 max-w-[calc(100cqw-2.5rem)] overflow-hidden @[40rem]/index-table:max-w-none',
          getIndexTableCellAlignClassName(contentAlign),
          contentClassName
        )}
      >
        {children}
      </div>
    </>
  )
  const interactiveCellContentClassName =
    "flex w-full min-w-0 flex-col px-4 py-2 after:absolute after:inset-0 after:content-[''] @[40rem]/index-table:flex-row @[40rem]/index-table:items-center @[40rem]/index-table:px-3 @[40rem]/index-table:py-3"
  const staticCellContentClassName =
    'px-4 py-2 @[40rem]/index-table:flex @[40rem]/index-table:items-center @[40rem]/index-table:px-3 @[40rem]/index-table:py-3'
  const overlayCellContentClassName =
    'flex w-full min-w-0 flex-col px-4 py-2 @[40rem]/index-table:flex-row @[40rem]/index-table:items-center @[40rem]/index-table:px-3 @[40rem]/index-table:py-3'
  const focusClassName =
    'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
  const fullCellFocusClassName =
    'focus-visible:outline-none focus-visible:after:rounded-sm focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring'

  if (link && linkMode === 'overlay') {
    return (
      <td
        className={cn(
          'relative block w-full min-w-0 align-middle @[40rem]/index-table:w-auto @[40rem]/index-table:table-cell',
          getIndexTableCellAlignClassName(contentAlign),
          className
        )}
      >
        <div
          className={cn(overlayCellContentClassName, getIndexTableCellPlacementClassName(align))}
        >
          <Link
            {...link}
            draggable={false}
            aria-hidden="true"
            tabIndex={-1}
            className={cn('absolute inset-0 z-0 cursor-pointer', focusClassName, linkClassName)}
          />
          <div className="pointer-events-none relative z-10 flex w-full min-w-0 flex-col">
            <IndexTableMobileLabel>{label}</IndexTableMobileLabel>
            {/* Stacked table rows size to the card container, not the viewport, because sidebars can shrink the content area before the table query. */}
            <div
              className={cn(
                'w-full min-w-0 max-w-[calc(100cqw-2.5rem)] overflow-hidden @[40rem]/index-table:max-w-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[role=button]]:pointer-events-auto',
                getIndexTableCellAlignClassName(contentAlign),
                contentClassName
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </td>
    )
  }

  return (
    <td
      className={cn(
        'relative block w-full min-w-0 align-middle @[40rem]/index-table:w-auto @[40rem]/index-table:table-cell',
        getIndexTableCellAlignClassName(contentAlign),
        className
      )}
    >
      {link ? (
        <Link
          {...link}
          draggable={false}
          className={cn(
            interactiveCellContentClassName,
            fullCellFocusClassName,
            'group/index-table-cell [&>*]:pointer-events-none',
            getIndexTableCellPlacementClassName(align),
            linkClassName
          )}
        >
          {content}
        </Link>
      ) : (
        <div className={cn(staticCellContentClassName, getIndexTableCellPlacementClassName(align))}>
          {content}
        </div>
      )}
    </td>
  )
}

function getIndexDataTableHeaderText<TData>(header: Header<TData, unknown>): string {
  const meta: ColumnMeta<TData, unknown> = header.column.columnDef.meta ?? {}
  const headerValue = header.column.columnDef.header

  if (['actions', 'select'].includes(header.column.id) || meta.kind === 'actions') {
    return ''
  }

  if (headerValue === '') {
    return ''
  }

  if (meta.label) {
    return meta.label
  }

  if (typeof headerValue === 'string') {
    return headerValue
  }

  return header.column.id
}

export type IndexDataTableHeaderProps<TData> = {
  table: Table<TData>
  /** The current server sort, matched against each column's `meta.sortable` values. */
  sort?: string | null
  onSortChange?: (sort: string) => void
}

export function IndexDataTableHeader<TData>({
  table,
  sort,
  onSortChange,
}: IndexDataTableHeaderProps<TData>) {
  return (
    <thead className="hidden border-b bg-elevated/80 @[40rem]/index-table:table-header-group">
      {table.getHeaderGroups().map((headerGroup) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const meta: ColumnMeta<TData, unknown> = header.column.columnDef.meta ?? {}
            const align = meta.headerAlign ?? meta.align ?? 'start'
            const label = getIndexDataTableHeaderText(header)
            const sortState = meta.sortable
              ? getDirectionalSortState(sort, meta.sortable)
              : undefined
            const clientSortDirection = header.column.getIsSorted()
            const clientSortState =
              !meta.sortable && header.column.getCanSort()
                ? {
                    active: Boolean(clientSortDirection),
                    direction: clientSortDirection || null,
                  }
                : undefined
            const headerContent = header.isPlaceholder ? null : typeof header.column.columnDef
                .header === 'function' && !header.column.getCanSort() ? (
              flexRender(header.column.columnDef.header, header.getContext())
            ) : sortState ? (
              <SortableColumnHeader
                icon={meta.icon}
                wrap={meta.headerWrap}
                active={sortState.active}
                direction={sortState.direction}
                className={cn(getIndexTableSortableHeaderClassName(align), meta.sortClassName)}
                onClick={() => onSortChange?.(sortState.nextSort)}
              >
                {label}
              </SortableColumnHeader>
            ) : clientSortState ? (
              <SortableColumnHeader
                icon={meta.icon}
                wrap={meta.headerWrap}
                active={clientSortState.active}
                direction={clientSortState.direction}
                className={cn(getIndexTableSortableHeaderClassName(align), meta.sortClassName)}
                onClick={header.column.getToggleSortingHandler()}
              >
                {label}
              </SortableColumnHeader>
            ) : (
              <SortableColumnHeader
                icon={meta.icon}
                wrap={meta.headerWrap}
                className={cn(getIndexTableSortableHeaderClassName(align), meta.sortClassName)}
              >
                {label}
              </SortableColumnHeader>
            )

            return (
              <IndexTableTableHeaderCell
                key={header.isPlaceholder ? header.id : header.column.id}
                colSpan={header.colSpan}
                scope={header.subHeaders.length && !header.isPlaceholder ? 'colgroup' : 'col'}
                align={align}
                tone={meta.headerTone}
                className={cn(meta.headerClassName, meta.headClassName)}
              >
                {meta.tooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block">{headerContent}</span>
                    </TooltipTrigger>
                    <TooltipContent className={meta.tooltipClassName}>
                      {meta.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  headerContent
                )}
              </IndexTableTableHeaderCell>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}

function IndexDataTableColGroup<TData>({ table }: { table: Table<TData> }) {
  const columns = table.getVisibleLeafColumns()

  return (
    <colgroup className="hidden @[40rem]/index-table:table-column-group">
      {columns.map((column) => {
        const meta: ColumnMeta<TData, unknown> = column.columnDef.meta ?? {}
        const preferredWidth = getIndexDataTablePreferredWidth(column)

        return (
          <col
            key={column.id}
            className={meta.colClassName}
            style={preferredWidth ? { width: preferredWidth } : undefined}
          />
        )
      })}
    </colgroup>
  )
}

function IndexDataTableCell<TData>({
  cell,
  link,
}: {
  cell: Cell<TData, unknown>
  link?: AppLinkProps | null
}) {
  const meta: ColumnMeta<TData, unknown> = cell.column.columnDef.meta ?? {}
  const cellLink = meta.link === false ? undefined : link

  return (
    <IndexTableTableCell
      label={meta.mobileLabel ?? meta.label ?? cell.column.id}
      link={cellLink}
      linkMode={meta.link === 'overlay' ? 'overlay' : 'wrap'}
      align={meta.align ?? 'start'}
      contentAlign={meta.contentAlign ?? meta.align ?? 'start'}
      className={meta.cellClassName}
      contentClassName={meta.contentClassName}
      linkClassName={meta.linkClassName}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </IndexTableTableCell>
  )
}

function IndexDataTableLoadingRows<TData>({
  columns,
  rowCount,
}: {
  columns: Column<TData, unknown>[]
  rowCount: number
}) {
  return Array.from({ length: rowCount }, (_, rowIndex) => (
    <tr
      key={`loading-row-${rowIndex}`}
      className="block w-full border-b text-sm last:border-b-0 @[40rem]/index-table:table-row"
      aria-hidden="true"
    >
      {columns.map((column, columnIndex) => {
        const meta: ColumnMeta<TData, unknown> = column.columnDef.meta ?? {}
        const skeletonWidth =
          meta.loadingWidth ??
          indexDataTableSkeletonWidths[
            (rowIndex + columnIndex) % indexDataTableSkeletonWidths.length
          ]

        return (
          <IndexTableTableCell
            key={column.id}
            label={meta.mobileLabel ?? meta.label ?? column.id}
            align={meta.align ?? 'start'}
            contentAlign={meta.contentAlign ?? meta.align ?? 'start'}
            className={meta.cellClassName}
            contentClassName={meta.contentClassName}
          >
            <Skeleton className={cn('h-4 max-w-full', skeletonWidth)} />
          </IndexTableTableCell>
        )
      })}
    </tr>
  ))
}

function areIndexDataTableColumnsStable<TData>(
  previousColumns: Column<TData, unknown>[],
  nextColumns: Column<TData, unknown>[]
): boolean {
  if (previousColumns.length !== nextColumns.length) {
    return false
  }

  return previousColumns.every((column, index) => {
    const nextColumn = nextColumns[index]

    return (
      nextColumn !== undefined &&
      column.id === nextColumn.id &&
      column.columnDef.cell === nextColumn.columnDef.cell &&
      column.columnDef.meta === nextColumn.columnDef.meta
    )
  })
}

// memo() types its result for a single props type, dropping a component's type parameters.
// Returning it through this signature keeps them. Like every memo result, it is for JSX only.
function memoGeneric<TProps extends object>(
  component: (props: TProps) => ReactNode,
  propsAreEqual?: (previousProps: TProps, nextProps: TProps) => boolean
): (props: TProps) => ReactNode {
  return memo(component, propsAreEqual)
}

type IndexDataTableRowProps<TData> = {
  row: Row<TData>
  /** Read only by the memo comparison; cells come from the row. */
  columns: Column<TData, unknown>[]
  isSelected: boolean
  rowLink?: (row: TData) => AppLinkProps | null | undefined
  getRowPresentation?: (row: TData) => IndexDataTableRowPresentation | null | undefined
  onRowClick?: (row: TData) => void
}

function areIndexDataTableRowPropsEqual<TData>(
  previousProps: IndexDataTableRowProps<TData>,
  nextProps: IndexDataTableRowProps<TData>
): boolean {
  return (
    previousProps.row.id === nextProps.row.id &&
    previousProps.row.original === nextProps.row.original &&
    previousProps.isSelected === nextProps.isSelected &&
    previousProps.rowLink === nextProps.rowLink &&
    previousProps.getRowPresentation === nextProps.getRowPresentation &&
    previousProps.onRowClick === nextProps.onRowClick &&
    // TanStack rows read the current columns, even on the previous row object.
    // Compare the columns captured by each render so stateful cells can update.
    areIndexDataTableColumnsStable(previousProps.columns, nextProps.columns)
  )
}

const IndexDataTableMemoRow = memoGeneric(function IndexDataTableMemoRow<TData>({
  row,
  isSelected,
  rowLink,
  getRowPresentation,
  onRowClick,
}: IndexDataTableRowProps<TData>) {
  const original = row.original
  const link = rowLink?.(original)
  const presentation = normalizeIndexDataTableRowPresentation(getRowPresentation?.(original))
  const rowIsSelected = isSelected || presentation.selected

  return (
    <tr
      data-state={rowIsSelected ? 'selected' : undefined}
      data-tone={presentation.tone}
      data-attention={presentation.attention === 'unread' ? 'unread' : undefined}
      data-content-tone={presentation.contentTone === 'muted' ? 'muted' : undefined}
      className={cn(
        'block w-full border-b border-l-4 border-l-transparent text-sm last:border-b-0 @[40rem]/index-table:table-row',
        onRowClick && 'cursor-pointer',
        getIndexDataTableRowPresentationClassName({
          ...presentation,
          selected: rowIsSelected,
        })
      )}
      onClick={onRowClick ? () => onRowClick(original) : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <IndexDataTableCell key={cell.id} cell={cell} link={link} />
      ))}
    </tr>
  )
}, areIndexDataTableRowPropsEqual)

export function getClientIndexDataTablePageSizeOptions(
  maxPageSize: number,
  currentPageSize?: number
): number[] {
  const pageSizes: number[] = []

  for (let pageSize = 10; pageSize < maxPageSize; pageSize += 10) {
    pageSizes.push(pageSize)
  }

  pageSizes.push(maxPageSize)

  if (currentPageSize && !pageSizes.includes(currentPageSize)) {
    pageSizes.push(currentPageSize)
    pageSizes.sort((left, right) => left - right)
  }

  return pageSizes
}

type ClientIndexDataTablePaginationProps<TData> = {
  table: Table<TData>
  maxPageSize: number
}

function ClientIndexDataTablePagination<TData>({
  table,
  maxPageSize,
  syncToUrl,
}: ClientIndexDataTablePaginationProps<TData> & { syncToUrl: boolean }) {
  if (syncToUrl) {
    return <ClientIndexDataTableUrlPagination table={table} maxPageSize={maxPageSize} />
  }

  return <ClientIndexDataTablePaginationControls table={table} maxPageSize={maxPageSize} />
}

function ClientIndexDataTablePaginationControls<TData>({
  table,
  maxPageSize,
}: ClientIndexDataTablePaginationProps<TData>) {
  const pagination = table.getState().pagination
  const pageCount = table.getPageCount()
  const normalizedPageIndex = pagination.pageIndex >= pageCount ? 0 : pagination.pageIndex
  const pageSizeOptions = getClientIndexDataTablePageSizeOptions(maxPageSize, pagination.pageSize)
  const meta = {
    current_page: normalizedPageIndex + 1,
    page_size: pagination.pageSize,
    total: table.getPrePaginationRowModel().rows.length,
    last_page: pageCount,
  }

  return (
    <IndexTablePagination
      meta={meta}
      pageSize={pagination.pageSize}
      onPageChange={(page) => table.setPageIndex(page - 1)}
      onPageSizeChange={(pageSize) => table.setPageSize(pageSize)}
      pageSizeOptions={pageSizeOptions}
      summary={`${meta.total} row(s)`}
    />
  )
}

function ClientIndexDataTableUrlPagination<TData>({
  table,
  maxPageSize,
}: ClientIndexDataTablePaginationProps<TData>) {
  const navigate = useNavigate()
  const urlSearch = useSearch({ strict: false })
  const { pathname } = useLocation()
  const pagination = table.getState().pagination
  const pageCount = table.getPageCount()
  const normalizedPageIndex = pagination.pageIndex >= pageCount ? 0 : pagination.pageIndex
  const pageSizeOptions = getClientIndexDataTablePageSizeOptions(maxPageSize, pagination.pageSize)
  const meta = {
    current_page: normalizedPageIndex + 1,
    page_size: pagination.pageSize,
    total: table.getPrePaginationRowModel().rows.length,
    last_page: pageCount,
  }
  const normalizedUrlPage = Number(urlSearch.page ?? 1)
  const normalizedUrlPageSize = Number(urlSearch.pageSize ?? pagination.pageSize)
  const hasValidUrlPage =
    Number.isFinite(normalizedUrlPage) && normalizedUrlPage > 0 && normalizedUrlPage <= pageCount
  const hasValidUrlPageSize =
    Number.isFinite(normalizedUrlPageSize) &&
    normalizedUrlPageSize > 0 &&
    normalizedUrlPageSize <= maxPageSize

  useEffect(() => {
    if (!hasValidUrlPage && !hasValidUrlPageSize) {
      return
    }

    const nextPageIndex = hasValidUrlPage ? normalizedUrlPage - 1 : normalizedPageIndex
    const nextPageSize = hasValidUrlPageSize ? normalizedUrlPageSize : pagination.pageSize

    if (nextPageIndex !== pagination.pageIndex) {
      table.setPageIndex(nextPageIndex)
    }

    if (nextPageSize !== pagination.pageSize) {
      table.setPageSize(nextPageSize)
    }
  }, [
    hasValidUrlPage,
    hasValidUrlPageSize,
    normalizedUrlPage,
    normalizedUrlPageSize,
    normalizedPageIndex,
    pageCount,
    pagination.pageIndex,
    pagination.pageSize,
    table,
  ])

  useEffect(() => {
    if (window.location.pathname !== pathname) {
      return
    }

    if (
      Number(urlSearch.page) === normalizedPageIndex + 1 &&
      Number(urlSearch.pageSize) === pagination.pageSize
    ) {
      return
    }

    if (
      (hasValidUrlPage && normalizedUrlPage - 1 !== pagination.pageIndex) ||
      (hasValidUrlPageSize && normalizedUrlPageSize !== pagination.pageSize)
    ) {
      return
    }

    if (normalizedPageIndex !== pagination.pageIndex) {
      table.setPageIndex(normalizedPageIndex)
    }

    void navigate({
      // '.' is what an omitted `to` resolves to; naming it lets the router type the search.
      to: '.',
      search: {
        ...urlSearch,
        page: normalizedPageIndex + 1,
        pageSize: pagination.pageSize,
      },
      replace: true,
      resetScroll: false,
    })
  }, [
    hasValidUrlPage,
    hasValidUrlPageSize,
    navigate,
    normalizedPageIndex,
    normalizedUrlPage,
    normalizedUrlPageSize,
    pagination.pageIndex,
    pagination.pageSize,
    pathname,
    table,
    urlSearch,
  ])

  const navigateToPagination = ({ page, pageSize }: { page: number; pageSize: number }) => {
    void navigate({
      to: '.',
      search: {
        ...urlSearch,
        page,
        pageSize,
      },
      replace: true,
      resetScroll: false,
    })
  }

  return (
    <IndexTablePagination
      meta={meta}
      pageSize={pagination.pageSize}
      onPageChange={(page) => navigateToPagination({ page, pageSize: pagination.pageSize })}
      onPageSizeChange={(pageSize) => navigateToPagination({ page: 1, pageSize })}
      pageSizeOptions={pageSizeOptions}
      summary={`${meta.total} row(s)`}
    />
  )
}

export type IndexDataTableProps<TData> = {
  data?: TData[]
  /**
   * Object-literal definitions (`ColumnDef<Row>[]`). `columnHelper.accessor` returns
   * value-typed definitions that TanStack's types do not widen to this.
   */
  columns?: ColumnDef<TData>[]
  /** The current server sort, matched against each column's `meta.sortable` values. */
  sort?: string | null
  onSortChange?: (sort: string) => void
  initialSorting?: SortingState
  rowLink?: (row: TData) => AppLinkProps | null | undefined
  getRowPresentation?: (row: TData) => IndexDataTableRowPresentation | null | undefined
  getRowId?: TableOptions<TData>['getRowId']
  toolbar?: ReactNode
  emptyState?: ReactNode
  tableClassName?: string
  scrollAreaClassName?: string
  /** `client` sorts (and with `pagination`, pages) `data` in the browser. */
  mode?: 'server' | 'client'
  pagination?: boolean
  maxPageSize?: number
  defaultPageSize?: number
  syncPaginationToUrl?: boolean
  isLoading?: boolean
  loadingRowCount?: number
  enableSortingRemoval?: boolean
  enableRowSelection?: boolean
  getRowCanSelect?: (row: Row<TData>) => boolean
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  rowSelection?: RowSelectionState
  onRowClick?: (row: TData) => void
  columnVisibility?: VisibilityState
}

/**
 * Money columns (`meta.kind: 'money'`) sort by amount. API money is a decimal string ("1234.50"),
 * which TanStack's automatic sort compares as text, putting "9.00" above "10.00".
 */
export function sortByMoney<TData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): number {
  return readMoneyCell(rowA, columnId) - readMoneyCell(rowB, columnId)
}

function readMoneyCell<TData>(row: Row<TData>, columnId: string): number {
  const value: unknown = row.getValue(columnId)

  return typeof value === 'number' || typeof value === 'string' ? parseCurrency(value) : 0
}

// Client-mode tables sort money columns by amount, largest first like numbers; a column's own
// sortingFn or sortDescFirst still wins.
function withMoneySorting<TData>(columns: ColumnDef<TData>[]): ColumnDef<TData>[] {
  return columns.map((column) => {
    if ('columns' in column && column.columns) {
      return { ...column, columns: withMoneySorting(column.columns) }
    }

    return column.meta?.kind === 'money' && column.sortingFn === undefined
      ? { ...column, sortingFn: sortByMoney, sortDescFirst: column.sortDescFirst ?? true }
      : column
  })
}

/**
 * Shared admin index-table renderer.
 *
 * For server-backed pages, pair this with `useIndexTableSearchParams` for URL
 * state and `indexTableQueryOptions` for query defaults. That combination is
 * the intended path for future index pages; route-local search/pagination/query
 * wiring should be the exception, not the starting point.
 */
export function IndexDataTable<TData>({
  data = [],
  columns = [],
  sort,
  onSortChange,
  initialSorting = emptyIndexDataTableSorting,
  rowLink,
  getRowPresentation,
  getRowId,
  toolbar,
  emptyState,
  tableClassName,
  scrollAreaClassName,
  mode = 'server',
  pagination = false,
  maxPageSize = 100,
  defaultPageSize = 20,
  syncPaginationToUrl,
  isLoading = false,
  loadingRowCount = 5,
  enableSortingRemoval = false,
  enableRowSelection = false,
  getRowCanSelect,
  onRowSelectionChange,
  rowSelection,
  onRowClick,
  columnVisibility = {},
}: IndexDataTableProps<TData>) {
  const manualTableState = mode !== 'client'
  const shouldSyncPaginationToUrl = syncPaginationToUrl ?? false
  const normalizedDefaultPageSize = Math.max(1, Math.min(maxPageSize, defaultPageSize))
  const rowSelectionEnabled = Boolean(enableRowSelection || getRowCanSelect)
  const resolvedEnableRowSelection = getRowCanSelect ?? enableRowSelection
  const controlledTableState = {
    ...(Object.keys(columnVisibility).length ? { columnVisibility } : {}),
    ...(rowSelectionEnabled ? { rowSelection: rowSelection ?? {} } : {}),
  }
  const clientPaginationRowCount = mode === 'client' && pagination ? (data?.length ?? 0) : null
  // Keeps the caller's column identity, so TanStack doesn't rebuild its column model every render.
  const tableColumns = useMemo(
    () => (mode === 'client' ? withMoneySorting(columns) : columns),
    [columns, mode]
  )
  // TanStack Table is the shared index-table state engine; React Compiler skips this hook safely.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns: tableColumns,
    getRowId,
    ...(rowSelectionEnabled
      ? {
          enableRowSelection: resolvedEnableRowSelection,
          onRowSelectionChange,
        }
      : {}),
    ...(Object.keys(controlledTableState).length ? { state: controlledTableState } : {}),
    initialState: {
      sorting: initialSorting,
      pagination: {
        pageSize: pagination ? normalizedDefaultPageSize : Math.min(data?.length ?? 0, 1000),
        pageIndex: 0,
      },
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    ...(mode === 'client' && pagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    ...(mode === 'client' ? { getSortedRowModel: getSortedRowModel() } : {}),
    manualFiltering: manualTableState,
    manualPagination: manualTableState,
    manualSorting: manualTableState,
    enableSortingRemoval,
    autoResetPageIndex: !manualTableState,
  })

  useEffect(() => {
    if (clientPaginationRowCount === null) {
      return
    }

    const pageIndex = table.getState().pagination.pageIndex

    if (pageIndex >= table.getPageCount()) {
      table.setPageIndex(0)
    }
  }, [clientPaginationRowCount, table])

  const rows = table.getRowModel().rows
  const visibleColumns = table.getVisibleLeafColumns()
  const showInitialLoading = isLoading && rows.length === 0

  return (
    <>
      <IndexTable
        toolbar={toolbar}
        header={
          <>
            <IndexDataTableColGroup table={table} />
            <IndexDataTableHeader table={table} sort={sort} onSortChange={onSortChange} />
          </>
        }
        isEmpty={!showInitialLoading && rows.length === 0}
        emptyState={emptyState}
        layout="table"
        tableClassName={tableClassName}
        tableStyle={getIndexDataTableStyle(visibleColumns)}
        scrollAreaClassName={scrollAreaClassName}
        isLoading={showInitialLoading}
        quietBadges
      >
        <tbody className="block w-full @[40rem]/index-table:table-row-group">
          {showInitialLoading ? (
            <IndexDataTableLoadingRows columns={visibleColumns} rowCount={loadingRowCount} />
          ) : (
            rows.map((row) => (
              <IndexDataTableMemoRow
                key={row.id}
                row={row}
                columns={visibleColumns}
                isSelected={row.getIsSelected()}
                rowLink={rowLink}
                getRowPresentation={getRowPresentation}
                onRowClick={onRowClick}
              />
            ))
          )}
        </tbody>
      </IndexTable>
      {mode === 'client' && pagination ? (
        <ClientIndexDataTablePagination
          table={table}
          maxPageSize={maxPageSize}
          syncToUrl={shouldSyncPaginationToUrl}
        />
      ) : null}
    </>
  )
}

export type IndexTableRowLinkProps = {
  link: AppLinkProps
  children?: ReactNode
  columns?: readonly IndexTableColumnWidths[]
  className?: string
  rowClassName?: string
}

export function IndexTableRowLink({
  link,
  children,
  columns,
  className,
  rowClassName,
}: IndexTableRowLinkProps) {
  return (
    <Link
      {...link}
      draggable={false}
      className={cn(
        'block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className
      )}
    >
      <div
        className={cn(
          'grid gap-3 px-4 py-3 text-sm hover:bg-muted/70 @[40rem]/index-table:w-full @[40rem]/index-table:items-center',
          getIndexTableGridClassName(columns),
          rowClassName
        )}
        style={getIndexTableGridStyle(columns)}
      >
        {children}
      </div>
    </Link>
  )
}

export function IndexTableMobileLabel({ children }: { children?: ReactNode }) {
  return (
    <Typography variant="eyebrow" className="block @[40rem]/index-table:hidden">
      {children}
    </Typography>
  )
}

type IndexTableRowItem = { id: Key }

function getDefaultRowKey(item: IndexTableRowItem): Key {
  return item.id
}

type IndexTableMemoRowProps<TItem> = {
  item: TItem
  rowComponent: ComponentType<{ item: TItem }>
}

// Centralize row memoization so index pages get the cheap render path by default.
const IndexTableMemoRow = memoGeneric(function IndexTableMemoRow<TItem>({
  item,
  rowComponent: RowComponent,
}: IndexTableMemoRowProps<TItem>) {
  return <RowComponent item={item} />
})

export type IndexTableRowsProps<TItem extends IndexTableRowItem> = {
  items: readonly TItem[]
  rowComponent: ComponentType<{ item: TItem }>
  /** Defaults to `item.id`. */
  getRowKey?: (item: TItem, index: number) => Key
}

export function IndexTableRows<TItem extends IndexTableRowItem>({
  items,
  rowComponent,
  getRowKey = getDefaultRowKey,
}: IndexTableRowsProps<TItem>) {
  return items.map((item, index) => (
    <IndexTableMemoRow key={getRowKey(item, index)} item={item} rowComponent={rowComponent} />
  ))
}

export type IndexTablePaginationProps = {
  meta: IndexPaginationMeta
  pageSize: number
  onPageChange: (page: number) => void
  /** Hover or focus on a page button, for prefetching that page. */
  onPageIntent?: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizeOptions?: readonly number[]
  pageSizeLabel?: ReactNode
  summary?: ReactNode
  className?: string
}

export function IndexTablePagination({
  meta,
  pageSize,
  onPageChange,
  onPageIntent,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  pageSizeLabel = 'Rows per page',
  summary,
  className,
}: IndexTablePaginationProps) {
  const totalPages = meta.last_page || 1
  const firstPage = 1
  const previousPage = Math.max(1, meta.current_page - 1)
  const nextPage = Math.min(totalPages, meta.current_page + 1)
  const lastPage = totalPages
  const canGoPrevious = meta.current_page > 1
  const canGoNext = meta.current_page < totalPages
  // Rows can leave a list while someone is on a later page (deleted here or by a teammate),
  // leaving the URL past the end. Empty responses also return to page 1. getIndexPaginationMeta
  // marks a response that is still loading, so a loading deep link keeps its requested page.
  const isPastLastPage = !meta.isPlaceholder && meta.current_page > totalPages
  const stepBackToLastPage = useEffectEvent(() => onPageChange(totalPages))
  // Keyed on the target page too, so a list that shrinks again while the corrected page loads
  // gets corrected again.
  const stepBackTarget = isPastLastPage ? totalPages : null
  useEffect(() => {
    if (stepBackTarget !== null) {
      stepBackToLastPage()
    }
  }, [stepBackTarget])
  const getPageIntentProps = (page: number, disabled = false) => {
    if (!onPageIntent || disabled || page === meta.current_page) {
      return {}
    }

    return {
      onFocus: () => onPageIntent(page),
      onPointerEnter: () => onPageIntent(page),
    }
  }

  return (
    <div
      className={cn(
        'mt-3 flex flex-wrap items-center gap-x-4 gap-y-2',
        summary ? 'justify-between' : 'justify-end',
        className
      )}
    >
      {summary ? <div className="mr-auto text-sm text-muted-foreground">{summary}</div> : null}
      <div className="flex shrink-0 items-center gap-2">
        <span className="whitespace-nowrap text-sm font-medium">{pageSizeLabel}</span>
        <Select
          value={`${pageSize}`}
          onValueChange={(value) => {
            onPageSizeChange(Number(value))
          }}
        >
          <SelectTrigger className="w-20">
            <SelectValue placeholder={pageSize} />
          </SelectTrigger>
          <SelectContent side="top">
            {pageSizeOptions.map((option) => (
              <SelectItem key={option} value={`${option}`}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
        <div className="min-w-[88px] whitespace-nowrap text-center text-sm font-medium tabular-nums">
          Page {meta.current_page} of {totalPages}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button
            variant="outline"
            size="icon"
            className="hidden sm:flex"
            onClick={() => onPageChange(firstPage)}
            disabled={!canGoPrevious}
            {...getPageIntentProps(firstPage, !canGoPrevious)}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(previousPage)}
            disabled={!canGoPrevious}
            {...getPageIntentProps(previousPage, !canGoPrevious)}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(nextPage)}
            disabled={!canGoNext}
            {...getPageIntentProps(nextPage, !canGoNext)}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="hidden sm:flex"
            onClick={() => onPageChange(lastPage)}
            disabled={!canGoNext}
            {...getPageIntentProps(lastPage, !canGoNext)}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  )
}
